import * as React from 'react';

import {
  Input as AriaInput,
  NumberField as AriaNumberField,
  NumberFieldProps as AriaNumberFieldProps,
  ValidationResult as AriaValidationResult,
  composeRenderProps,
  Text,
} from 'react-aria-components';

import { cn, withColor } from '../../../lib/stylingUtils';
import { ThemeColor } from '../../../types/theme';
import { FieldError, FieldGroup, Label } from '../Field';
import {
  VirtualNumericKeyboard,
  VirtualNumericKeyboardExtraKey,
  VirtualNumericKeyboardRef,
} from '../keyboard/VirtualNumericKeyboard';

export type TouchNumberFieldProps = AriaNumberFieldProps & {
  label?: string;
  description?: string;
  errorMessage?: string | ((validation: AriaValidationResult) => string);
  className?: string;
  numberClassName?: string;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  preFocused?: boolean;
  virtualKeyboardExtraKey?: VirtualNumericKeyboardExtraKey;
  disableVirtualKeyboard?: boolean;
  touchModeEnabled?: boolean;
  minDecimalPlaces?: number;
  maxDecimalPlaces?: number;
  variant?: 'flat' | 'outlined' | 'subtle';
  color?: ThemeColor;
  onFocusChange?: (focused: boolean) => void;
  onKeyPress?: (key: string) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
};

export const TouchNumberField = ({
  label,
  description,
  errorMessage,
  className,
  numberClassName,
  prefix,
  suffix,
  preFocused,
  onKeyPress,
  onKeyDown,
  virtualKeyboardExtraKey,
  disableVirtualKeyboard,
  maxDecimalPlaces,
  touchModeEnabled = false,
  variant = 'flat',
  color,
  onFocusChange,
  ...props
}: TouchNumberFieldProps) => {
  const virtualKeyboardRef = React.useRef<VirtualNumericKeyboardRef>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (!preFocused) {
      virtualKeyboardRef.current?.dismiss();
    } else {
      virtualKeyboardRef.current?.open();
    }
  }, [preFocused]);

  React.useEffect(() => {
    onFocusChange?.(!!preFocused);
  }, [preFocused, onFocusChange]);

  const renderedPrefix = React.useMemo(() => {
    if (!prefix) return null;
    if (!color) return prefix;
    return (
      <span className="text-custom-color/60 group-focus-within:text-neutral/40">
        {prefix}
      </span>
    );
  }, [prefix, color]);

  const renderedSuffix = React.useMemo(() => {
    if (!suffix) return null;
    if (!color) return suffix;
    return (
      <span className="text-custom-color/60 group-focus-within:text-neutral/40">
        {suffix}
      </span>
    );
  }, [suffix, color]);

  return (
    <div
      className={cn(
        '@TouchNumberField group flex h-full border-r-0 border-solid border-neutral/20',
        className,
      )}
      style={withColor(color ?? 'neutral')}
    >
      <VirtualNumericKeyboard
        ref={virtualKeyboardRef}
        maxDecimalPlaces={maxDecimalPlaces}
        enabled={touchModeEnabled && !disableVirtualKeyboard}
        onFocus={props.onFocus as never}
        onChange={props.onChange as never}
        value={props.value}
        onKeyPress={onKeyPress}
        extraKey={virtualKeyboardExtraKey}
      >
        <AriaNumberField
          isInvalid={!!errorMessage}
          className={composeRenderProps(numberClassName, (numberClassName) =>
            cn(
              '@TouchNumberFieldNumber',
              'group flex w-full flex-col gap-2',
              numberClassName,
            ),
          )}
          {...props}
        >
          {label && <Label>{label}</Label>}
          <FieldGroup
            prefix={renderedPrefix}
            suffix={renderedSuffix}
            isDisabled={props.isDisabled}
            className={cn(
              '!h-full !min-h-full p-0 py-[5px] pr-[8px] pl-[6px] text-sm text-neutral focus-within:bg-white',
              variant === 'outlined' && 'rounded-sm',
              variant === 'outlined' &&
                !props.isDisabled &&
                'border-custom-color/20 bg-white',
              variant === 'outlined' &&
                props.isDisabled &&
                'border-transparent bg-custom-color/5',
              variant !== 'outlined' &&
                'border-transparent bg-custom-color/5 rac-disabled:bg-white',
              variant === 'subtle' &&
                'border-transparent bg-custom-color/5 rac-disabled:bg-custom-color/5',
              color &&
                'bg-custom-color/10 text-custom-color focus-within:text-neutral',
            )}
          >
            <AriaInput
              ref={inputRef}
              onFocus={(e: React.FocusEvent<HTMLInputElement>) =>
                e.target.select()
              }
              onKeyDown={onKeyDown}
              className={cn(
                'text-right selection:bg-current/20',
                color && 'text-custom-color focus:text-neutral',
              )}
            />
          </FieldGroup>
          {description && (
            <Text className="text-sm text-foreground/20" slot="description">
              {description}
            </Text>
          )}
          <FieldError>{errorMessage}</FieldError>
        </AriaNumberField>
      </VirtualNumericKeyboard>
    </div>
  );
};
